import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchFromOldApi } from '~/services/Http';

import { CompanyData } from './types';

export enum TabModules {
  PEOPLE = 'PEOPLE',
  DEVELOPMENT = 'DEVELOPMENT',
  REWARDING = 'REWARDING',
  PERFORMANCE = 'PERFORMANCE',
}

export interface ModulesState {
  enabledModules: string[];
  enabledTabs: { [module: string]: string[] };
}

export type StoreState = {
  companyData: CompanyData;
  modules: ModulesState;
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
};

const initialState: StoreState = {
  companyData: {} as CompanyData,
  modules: {
    enabledModules: [],
    enabledTabs: {
      [TabModules.PEOPLE]: [],
      [TabModules.DEVELOPMENT]: [],
      [TabModules.REWARDING]: [],
      [TabModules.PERFORMANCE]: [],
    },
  },
  isLoading: false,
  failedToLoad: false,
  hasData: false,
};

export enum CompanyDataActionKeyPrefixes {
  FETCH_COMPANY_DATA = 'company/FETCH_COMPANY_DATA',
}

export type CompanyDataPayload = {
  companyData: CompanyData;
  modules: ModulesState;
};

export const getCompanyData = (): Promise<CompanyData> =>
  fetchFromOldApi('/json/company').then((r) => r.json());

export const getEnabledTabsForModule = (module: string): Promise<string[]> =>
  fetchFromOldApi(`/json/company/ENABLED_TABS_${module}/tabs`).then((r) =>
    r.json(),
  );

export const fetchCompanyData = createAsyncThunk<CompanyDataPayload>(
  CompanyDataActionKeyPrefixes.FETCH_COMPANY_DATA,
  async (): Promise<CompanyDataPayload> => {
    const enabledModulesPromise = () =>
      fetchFromOldApi('/json/module').then((r) => r.json());

    const enabledTabsPromise = () =>
      Promise.all([
        getEnabledTabsForModule(TabModules.PEOPLE),
        getEnabledTabsForModule(TabModules.DEVELOPMENT),
        getEnabledTabsForModule(TabModules.REWARDING),
        getEnabledTabsForModule(TabModules.PERFORMANCE),
      ]).then((values) => ({
        PEOPLE: values[0],
        DEVELOPMENT: values[1],
        REWARDING: values[2],
        PERFORMANCE: values[3],
      }));

    return Promise.all([
      getCompanyData(),
      enabledModulesPromise(),
      enabledTabsPromise(),
    ]).then(
      ([companyData, enabledModules, enabledTabs]): CompanyDataPayload => {
        return {
          companyData,
          modules: {
            enabledModules,
            enabledTabs,
          },
        };
      },
    );
  },
);

export const companyDataSlice = createSlice({
  name: '~/data/company',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchCompanyData.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<CompanyDataPayload>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.hasData = true;
      state.companyData = action.payload.companyData;
      state.modules = action.payload.modules;
    },
    [fetchCompanyData.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchCompanyData.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const companyDataReducer = companyDataSlice.reducer;
